import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { StoreContext } from '../contexts/StoreContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import { ToastContainer, toast } from 'react-toastify';
import { Container } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';
import 'react-toastify/dist/ReactToastify.css';
import './AddOrEditProduct.css';
import ThumbnailRearrange from '../components/ThumbnailRearrange';
import debounce from 'lodash.debounce';
import DragDropImageUpload from '../components/DragDropImageUpload';

const STORE_API_URL = process.env.REACT_APP_API_URL;
const ELECTRON_URL = process.env.REACT_APP_ELECTRON_URL

function AddOrEditProduct({ isEdit, defaultType = 'showcase' }) {
  const { id } = useParams();
  const { token, isAuthenticated, role } = useContext(AuthContext); 
  const { store, stores, selectedStore, setSelectedStore, setStoreLocalStorage, setStoreName, setStoreLogo } = useContext(StoreContext);
  const navigate = useNavigate();
  const [product, setProduct] = useState({
    name: '',
    slug: '',
    intro: '',
    description: '',
    emailTemplate: '',
    price: 0,
    weight: 0,
    category: '',
    serviceCategory: '',
    author_name: '',
    serviceManual: '',
    brand: '',
    model: '',
    crt_models: [], // Array for multiple CRT models
    affiliateLink: '',
    affiliateCurrency: '',
    videos: [],
    images: [], // Set images as an array to hold multiple images
    inventory: 0,
    priority: 0,
    type: defaultType,
    stripeCustomField: { label: '', optional: true, type: 'text' },
    availability: 'local_and_online',
    published: false, 
    storePublished: false,
    showcaseCategory: '', // New field for showcase categories
    enablePaypalButton: false, // New field for enabling PayPal button
    paypalEmail: '', // New field for PayPal email
    showCrtWarning: true, // New field for CRT warning, default to true
  });

  // Store the initial product data for comparison in auto-save
  const [initialProduct, setInitialProduct] = useState(null);

  const [imagePreviews, setImagePreviews] = useState([]); // Previews for selected images
  const [removedImages, setRemovedImages] = useState([]); // State to hold images marked for soft delete
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'

  const [modelInput, setModelInput] = useState('');
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const getProgressColor = (progress) => {
    if (progress < 50) return '#f44336'; // Red
    if (progress < 75) return '#ff9800'; // Orange
    return '#4caf50'; // Green
  };

  useEffect(() => {
    if (selectedStore && isAuthenticated) {
      if (selectedStore === 'all' || selectedStore === 'orphaned') {
        setSelectedStore(store._id);
        setStoreLocalStorage(store._id)
      }
      // If editing a product, fetch the product details
      if (isEdit && id) {
        fetchProduct(id);
      }
    }
  }, [selectedStore]);

  useEffect(() => {
    // Always update slug when name changes
    if (product.name) {
      setProduct(prev => ({ ...prev, slug: generateSlug(product.name) }));
    }
  }, [product.name]);

  // Fields that should trigger auto-save
  const autoSaveFields = [
    'name',
    'intro',
    'description',
    'author_name',
    'service_manual',
    'crt_models',
    'model',
    'images',
    'videos',
    'email_template',
    'affiliate_link',
    'show_crt_warning'
  ];

  useEffect(() => {
    if (!isEdit || !product._id) return; // Only auto-save if editing an existing product
    
    // Check if any of the auto-save fields have changed
    const hasAutoSaveFieldsChanged = autoSaveFields.some(field => {
      return !deepEqual(product[field], initialProduct?.[field]);
    });

    if (!hasAutoSaveFieldsChanged) return; // Don't auto-save if no relevant fields changed

    setSaveStatus('idle'); // Reset to idle when changes are made
    
    const timeout = setTimeout(() => {
      autoSaveProduct();
    }, 5000); // Debounce: 5s after last change
    
    return () => clearTimeout(timeout);
  }, [autoSaveFields.map(field => product[field])]);

  // Deep equality check helper
  const deepEqual = (obj1, obj2) => {
    if (obj1 === obj2) return true;
    if (typeof obj1 !== typeof obj2) return false;
    if (typeof obj1 !== 'object') return obj1 === obj2;
    if (obj1 === null || obj2 === null) return obj1 === obj2;
    if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;
    
    const keys1 = Object.keys(obj1 || {});
    const keys2 = Object.keys(obj2 || {});
    if (keys1.length !== keys2.length) return false;
    
    return keys1.every(key => deepEqual(obj1[key], obj2[key]));
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[\s]+/g, '-').replace(/[^a-z0-9\-]/g, '');
  };

  // Auto-save function
  const autoSaveProduct = async () => {
    if (!product._id) return; // Only auto-save if we have an ID
    
    setSaveStatus('saving');
    
    try {
      // Create a payload with only auto-save fields
      const autoSavePayload = {
        ...autoSaveFields.reduce((acc, field) => ({
          ...acc,
          [field]: product[field]
        }), {}),
        store_id: product.store_id || null,
        removed_images: removedImages,
      };

      const response = await fetch(`${STORE_API_URL}/products/${product._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(autoSavePayload),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Error auto-saving product');
      }
      
      setSaveStatus('saved');
      
      // Reset to idle after a delay
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
      
    } catch (error) {
      console.error('Auto-save error:', error);
      setSaveStatus('error');
    }
  };

  const handleStoreChange = (e) => {
    const newStoreId = e.target.value;
    setSelectedStore(newStoreId);
    setStoreLocalStorage(newStoreId)
  
    const selected = stores.find(store => store._id === newStoreId);
    if (selected) {
      setStoreLogo(selected.logo || '');
      setStoreName(selected.name || ''); // Update storeName here
      console.log('Updated storeName: ' + selected.name + ' storeId: ' + newStoreId);
    }
  };

  const fetchProduct = async (productId) => {
    setLoading(true);
    setError(null);

    console.log('Fetching product ' + productId);

    try {
      const response = await fetch(`${STORE_API_URL}/products/id/${productId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Error fetching product.');
      }
      const productData = await response.json();

      // Map snake_case fields to camelCase for usage in the state
      const formattedProduct = {
        ...productData,
        price: (productData.price / 100).toFixed(2), 
        serviceCategory: productData.service_category,
        showcaseCategory: productData.showcase_category,
        serviceManual: productData.service_manual,
        affiliateLink: productData.affiliate_link,
        affiliateCurrency: productData.affiliate_currency,
        emailTemplate: productData.email_template,
        createdAt: productData.created_at, 
        updatedAt: productData.updated_at,
        videos: productData.videos || [],   
        images: productData.images || [],
        stripeCustomField: productData.stripe_custom_field || { label: '', optional: true, type: 'text' },
        availability: productData.availability || 'local_and_online',
        published: productData.published || false,
        storePublished: productData.store_published || false,
        enablePaypalButton: productData.enable_paypal_button || false,
        paypalEmail: productData.paypal_email || '',
        crt_models: productData.crt_models || [],
        showCrtWarning: productData.show_crt_warning !== undefined ? productData.show_crt_warning : true, // Default to true if not set
      };

      setProduct(formattedProduct);
      setInitialProduct(formattedProduct); // Store initial state
      setImagePreviews(productData.images || []);

      console.log('Product data: ' + JSON.stringify(productData));
      
      // If product has a model but no crt_models, initialize the array with the single model
      if (productData.model && (!productData.crt_models || productData.crt_models.length === 0)) {
        const updatedProduct = {
          ...formattedProduct,
          crt_models: [{
            name: productData.model,
            model: productData.model,
            slug: productData.model_slug || null
          }]
        };
        setProduct(updatedProduct);
        setInitialProduct(updatedProduct);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelection = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    const currentImages = product.images || [];
  
    if (selectedFiles.length + currentImages.length > 100) {
      toast.error("You can upload a maximum of 100 images.");
      return;
    }
  
    if (!product._id) {
      toast.error('Product ID is not available yet. Please save the product first.');
      return;
    }
  
    setLoading(true);
    setUploadProgress(0);
  
    const uploadedPreviews = [];
  
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const formData = new FormData();
      formData.append('image', file);
  
      try {
        const response = await fetch(`${STORE_API_URL}/upload/${selectedStore}/products/${product._id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });
  
        const data = await response.json();
  
        if (response.ok && data.image) {
          uploadedPreviews.push({
            ...data.image,
            caption: '',
          });
        } else {
          throw new Error(data.error || 'Error uploading file.');
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}: ${error.message}`);
      }
  
      setUploadProgress(Math.round(((i + 1) / selectedFiles.length) * 100));
    }
  
    const newImageList = [...product.images, ...uploadedPreviews];
  
    setImagePreviews(newImageList);
    setProduct((prevProduct) => ({
      ...prevProduct,
      images: newImageList,
    }));
  
    // Trigger automatic product update after images upload with ALL images
    await updateProductImages(newImageList);
  
    setLoading(false);
  };
  

  const handleInputChange = (e) => {
    const { name, value } = e.target;
  
    if (value < 0) {
      return;
    }

    if (name === 'intro' && value.length > 300) {
      return; // Prevent exceeding 300 characters
    }

    // For all input changes, just update the value
    if (name === 'price' || name === 'weight' || name === 'inventory' || name === 'priority') {
      // Allow raw input value and don't manipulate it right away
      if (value === '' || /^[0-9]*\.?[0-9]{0,2}$/.test(value)) {
        setProduct(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      // For non-numeric fields
      setProduct(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleCustomFieldChange = (e) => {
    const { name, value } = e.target;
    setProduct({
      ...product,
      stripeCustomField: {
        ...product.stripeCustomField,
        [name]: value
      }
    });
  };

  const handleCaptionChange = (index, value) => {
    setImagePreviews((prevPreviews) => {
      const updatedImages = [...prevPreviews];
      updatedImages[index] = {
        ...updatedImages[index],
        caption: value, // Update the caption directly
      };
  
      // Sync the changes with the product state
      setProduct((prevProduct) => ({
        ...prevProduct,
        images: updatedImages.map((img) => ({
          ...img,
          caption: img.caption || '', // Ensure all images include captions
        })),
      }));
  
      return updatedImages;
    });
  };

  const handleRemoveImageById = (id) => {
    const index = imagePreviews.findIndex(img => 
      img.key === id || `image-${imagePreviews.indexOf(img)}` === id
    );
    if (index !== -1) {
      handleRemoveImage(imagePreviews[index], index);
    }
  };

  const handleRemoveImage = async (image, index) => {
    // Check if user is allowed to delete images
    if (product.published && role !== 'super') {
      toast.error("You don't have permission to delete images from published products.");
      return;
    }
    
    // Remove the confirmation dialog and proceed directly with deletion
    const updatedImagePreviews = imagePreviews.filter((_, i) => i !== index);
    const updatedRemovedImages = image.key && !image.file
      ? [...removedImages, image]
      : removedImages;
  
    setImagePreviews(updatedImagePreviews);
    setRemovedImages(updatedRemovedImages);
  
    const updatedProductImages = product.images.filter((_, i) => i !== index);
    
    setProduct((prev) => ({
      ...prev,
      images: updatedProductImages,
    }));
  
    // Immediately update backend with changes
    await updateProductImages(updatedProductImages, updatedRemovedImages);
  };    

  const updateProductImages = async (updatedImages, removedImages = []) => {
    if (!product._id) {
      toast.error("Product ID missing, please save the product first.");
      return;
    }
  
    setLoading(true);
    setSaveStatus('saving');
  
    try {
      const response = await fetch(`${STORE_API_URL}/products/${product._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...product, // <-- Important: Include entire product object
          price: product.price * 100, // Ensure the price is properly formatted
          images: updatedImages,
          removed_images: removedImages,
          store_id: product.store_id || selectedStore,
          inventory: Number(product.inventory),
          service_manual: product.serviceManual,
          affiliate_currency: product.affiliateCurrency,
          service_category: product.serviceCategory,
          showcase_category: product.showcaseCategory,
          email_template: product.emailTemplate,
          stripe_custom_field: product.stripeCustomField,
          enable_paypal_button: product.enablePaypalButton,
          paypal_email: product.paypalEmail,
          crt_models: product.crt_models,
          show_crt_warning: product.showCrtWarning,
        }),
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.error || 'Error updating product images');
      }
  
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
      toast.success("Product images updated successfully!");
    } catch (error) {
      setSaveStatus('error');
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }; 

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSaveStatus('saving');
  
    try {
      // If there's at least one CRT model, use it as the primary model for backwards compatibility
      const productToSave = { ...product };
      if (productToSave.crt_models && productToSave.crt_models.length > 0) {
        productToSave.model = productToSave.crt_models[0].name || productToSave.crt_models[0].model;
      }
      
      const response = await fetch(isEdit ? `${STORE_API_URL}/products/${id}` : `${STORE_API_URL}/products`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...productToSave,
          price: productToSave.price * 100,
          images: imagePreviews,
          removed_images: removedImages,
          store_id: isEdit ? productToSave.store_id : (selectedStore || null), // Only use selectedStore for new products
          stripe_custom_field: productToSave.stripeCustomField,
          inventory: Number(productToSave.inventory),
          service_manual: productToSave.serviceManual,
          affiliate_currency: productToSave.affiliateCurrency,
          service_category: productToSave.serviceCategory,
          showcase_category: productToSave.showcaseCategory,
          email_template: productToSave.emailTemplate,
          enable_paypal_button: productToSave.enablePaypalButton,
          paypal_email: productToSave.paypalEmail,
          crt_models: productToSave.crt_models,
          show_crt_warning: productToSave.showCrtWarning,
          store_published: productToSave.storePublished
        }),
      });
  
      const responseData = await response.json();
  
      if (!response.ok) {
        setSaveStatus('error');
        throw new Error(responseData.error || 'Error saving product');
      }
  
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
      
      if (!isEdit && responseData.product_id) {
        setProduct((prev) => ({
          ...prev,
          _id: responseData.product_id,
          store_handle: store?.handle || ''
        }));
        navigate(`/edit-product/${responseData.product_id}`);
      }
  
      toast.success(responseData.message || 'Product saved successfully!');
    } catch (error) {
      setSaveStatus('error');
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };    

  const getPageTitle = () => {
    const type = product.type || defaultType;
    const base = isEdit ? 'Edit' : 'Add';
  
    switch (type) {
      case 'showcase':
        return isEdit ? 'Edit Showcase' : 'Add to Showcase';
      case 'service':
        return `${base} Service`;
      case 'affiliate':
        return `${base} Affiliate`;
      case 'sale':
      default:
        return `${base} Product`;
    }
  };

  const getButtonText = () => {
    const type = product.type || defaultType;
    const base = isEdit ? 'Update' : 'Add';
  
    switch (type) {
      case 'showcase':
        return isEdit ? 'Update Showcase' : 'Add to Showcase';
      case 'service':
        return `${base} Service`;
      case 'affiliate':
        return `${base} Affiliate`;
      case 'sale':
      default:
        return `${base} Product`;
    }
  };

  const debouncedFetch = useCallback(
    debounce(async (inputValue) => {
      if (inputValue.length < 2) {
        setSuggestions([]);
        return;
      }
  
      setIsLoadingModels(true);
  
      try {
        const res = await fetch(`${STORE_API_URL}/crts/autocomplete?q=${encodeURIComponent(inputValue)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
  
        const formatted = (data.suggestions || []).map((item) => ({
          value: item.title,
          label: item.title,
          slug: item.slug || null,
        }));
  
        setSuggestions(formatted);
      } catch (err) {
        console.error('Autocomplete error:', err);
        setSuggestions([]);
      } finally {
        setIsLoadingModels(false);
      }
    }, 500),
    [token]
  );
  
  // Cleanup debounce on unmount
  useEffect(() => {
    return () => debouncedFetch.cancel();
  }, [debouncedFetch]);  

  // Add script to handle sticky sidebar behavior on component mount
  useEffect(() => {
    if (isEdit) {
      // Create a placeholder element for position reference
      const placeholder = document.createElement('div');
      placeholder.style.visibility = 'hidden';
      placeholder.style.height = '0';
      placeholder.id = 'rearrange-placeholder';
      
      // Insert the placeholder before the rearrange section
      const rearrangeSection = document.getElementById('rearrange-section');
      if (rearrangeSection && rearrangeSection.parentNode) {
        rearrangeSection.parentNode.insertBefore(placeholder, rearrangeSection);
      }
      
      // Function to check if device is mobile
      const isMobile = () => window.innerWidth < 768;
      
      // Handle sticky behavior
      const handleScroll = () => {
        const rearrangeSection = document.getElementById('rearrange-section');
        const placeholder = document.getElementById('rearrange-placeholder');
        
        if (!rearrangeSection || !placeholder) return;
        
        // Skip sticky behavior on mobile devices
        if (isMobile()) {
          rearrangeSection.classList.remove('sticky');
          rearrangeSection.style.width = '';
          return;
        }
        
        // Calculate the original width once
        if (!rearrangeSection.dataset.originalWidth && rearrangeSection.offsetWidth) {
          rearrangeSection.dataset.originalWidth = rearrangeSection.offsetWidth + 'px';
        }
        
        // Get the position of the placeholder (original position)
        const placeholderRect = placeholder.getBoundingClientRect();
        
        // Simple sticky behavior based on placeholder position
        if (placeholderRect.top <= 60) {
          rearrangeSection.classList.add('sticky');
          if (rearrangeSection.dataset.originalWidth) {
            rearrangeSection.style.width = rearrangeSection.dataset.originalWidth;
          }
        } else {
          rearrangeSection.classList.remove('sticky');
          rearrangeSection.style.width = '';
        }
      };
      
      // Initial check
      handleScroll();
      
      // Add scroll listener
      window.addEventListener('scroll', handleScroll);
      
      // Also check on resize to handle orientation changes
      window.addEventListener('resize', handleScroll);
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
        // Clean up placeholder on unmount
        const placeholder = document.getElementById('rearrange-placeholder');
        if (placeholder && placeholder.parentNode) {
          placeholder.parentNode.removeChild(placeholder);
        }
      };
    }
  }, [isEdit]);

  return (
    <Container>
      <ToastContainer />
      <form id="productForm" onSubmit={handleSaveProduct}>
        <div className="headline d-flex justify-content-between align-items-center">
          <h2 className="underline">{getPageTitle()}</h2>
          {/* Store Filter */}
          {isEdit ? (
            <div className="save-status text-end">
              {saveStatus === 'saving' && <small className="text-muted">Saving…</small>}
              {saveStatus === 'saved' && <small className="text-success">All changes saved ✓</small>}
              {saveStatus === 'error' && <small className="text-danger">Failed to save ⚠</small>}
            </div>
          ) : (
            <div className="form-group">
              <label></label>
              <select
                id="storeSelect"
                className="form-select"
                value={selectedStore}
                onChange={handleStoreChange} // Use the handleStoreChange function
              >
                {stores.map((store) => (
                  <option key={store._id} value={store._id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <small className="text-muted">
          {product.createdAt ? 'Created: ' + new Date(product.createdAt).toLocaleString() : null}{product.updatedAt ? ' | Updated: ' + new Date(product.updatedAt).toLocaleString() : null}
        </small>

        <div className="text-end">
          {isEdit && !!product.store_handle && !!product.slug ? (
            <div className="d-flex justify-content-between align-items-center">
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <h6><a href={`${ELECTRON_URL}/${product.store_handle}/product/${product.slug}`} target='_blank'>Showcase Preview</a></h6>
                <h6><a href={`${ELECTRON_URL}/${product.store_handle}`} target="_blank" rel="noopener noreferrer">{product.store_name}</a></h6>
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    &nbsp; 'Updating...'
                  </>
                ) : (
                  getButtonText()
                )}
              </button>
            </div>
          ) : (
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  &nbsp;'Adding...'
                </>
              ) : (
                getButtonText()
              )}
            </button>
          )}
        </div>
        {/*loading && <div>Loading...</div>*/}
        {error && <div style={{ color: 'red' }}>{error}</div>}
        <div className="row product-form-container mt-3">
          <div className="col-md-12 col-lg-6">
            <h5 className="headline">Core Details</h5>
            {/* Form */}
              <div className="form-group">
                <label>Type</label>
                <select
                  name="type"
                  className="form-select"
                  value={product.type || ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    setProduct((prevProduct) => ({
                      ...prevProduct,
                      type: value,
                      affiliateLink: value !== 'affiliate' ? '' : prevProduct.affiliateLink, // Clear the link if type is not affiliate
                    }));
                  }}
                  required
                  disabled={defaultType === 'showcase'} // lock if coming from /add-showcase
                >
                  <option value="">Select type</option>
                  <option value="sale">For Sale</option>
                  <option value="showcase">Showcase</option>
                  <option value="service">Service</option>
                  <option value="affiliate">Affiliate</option>
                </select>
              </div>
              {role === 'super' && (
              <div className="form-group">
                <label>Published</label>
                <div style={{width:'100%'}}>
                  <input
                    type="checkbox"
                    name="published"
                    className="form-check-input me-2"
                    checked={product.published}
                    onChange={(e) => {
                      const { checked } = e.target;
                      setProduct((prevProduct) => ({
                        ...prevProduct,
                        published: checked,
                      }));
                    }}
                  />
                  <small className="form-text text-muted">
                    Toggle this to publish to the public (sunthar.com)
                  </small>
                </div>
              </div>
              )}
              <div className="form-group">
                <label>Published</label>
                <div style={{width:'100%'}}>
                  <input
                    type="checkbox"
                    name="storePublished"
                    className="form-check-input me-2"
                    checked={product.storePublished}
                    onChange={(e) => {
                      const { checked } = e.target;
                      setProduct((prevProduct) => ({
                        ...prevProduct,
                        storePublished: checked,
                      }));
                    }}
                  />
                  <small className="form-text text-muted">
                    Toggle this to publish to {store.name} 
                  </small>
                </div>
              </div>
              <div className="form-group">
                <label>Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  value={product.name || ''}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>
                  Slug <span className="text-danger">*</span>
                  <br />
                  <small className="form-text text-muted">URL-friendly name (auto-updates with name)</small>
                </label>
                <input 
                  type="text" 
                  name="slug" 
                  value={product.slug} 
                  onChange={(e) => setProduct(prev => ({ ...prev, slug: e.target.value }))}
                  className="form-control"
                  required
                />
              </div>
              {(product.type === 'sale') && (
              <>
                <div className="form-group">
                  <label>Product Category</label>
                  <select
                    name="category"
                    className="form-select"
                    value={product.category || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Product Category</option>
                    <option value="CRT TV">CRT TV</option>
                    <option value="CRT Mod Kits">CRT Mod Kits</option>
                    <option value="CRT TV Parts">CRT TV Parts</option>
                    <option value="Video Cables">Video Cables</option>
                    <option value="Audio Cables">Audio Cables</option>
                    <option value="CRT Accessories">CRT Accessories</option>
                    <option value="Game Consoles">Game Consoles</option>
                    <option value="Games & Cartridges">Games & Cartridges</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </>
              )}
              {(product.type === 'service') && (
              <div className="form-group">
                <label>Serive Category</label>
                <select
                  name="serviceCategory"
                  className="form-select"
                  value={product.serviceCategory || ''}
                  onChange={handleInputChange}
                >
                  <option value="">Select Service Category</option>
                  <option value="CRT TV repair">CRT TV repair</option>
                  <option value="CRT RGB mod installation">CRT RGB mod installation</option>
                  <option value="CRT other mods">CRT other mods</option>
                  <option value="Game console mods">Game console mods</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              )}
              {(product.type === 'showcase') && (
                <>
                  <div className="form-group">
                    <label>Showcase Category</label>
                    <select
                      name="showcaseCategory"
                      className="form-select"
                      value={product.showcaseCategory || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Showcase Category</option>
                      <option value="CRT">CRT</option>
                      <option value="Game Console">Game Console</option>
                      <option value="Camcorder">Camcorder</option>
                      <option value="Cassette Player">Cassette Player</option>
                      <option value="Camera">Camera</option>
                      <option value="Tape Player">Tape Player</option>
                      <option value="Laser Disc">Laser Disc</option>
                    </select>
                  </div>
                  {product.showcaseCategory === 'CRT' && role === 'super' && (
                    <div className="form-group">
                      <label>CRT Warning</label>
                      <div style={{width:'100%'}}>
                        <input
                          type="checkbox"
                          name="showCrtWarning"
                          className="form-check-input me-2"
                          checked={product.showCrtWarning}
                          onChange={(e) => {
                            const { checked } = e.target;
                            setProduct((prevProduct) => ({
                              ...prevProduct,
                              showCrtWarning: checked,
                            }));
                          }}
                        />
                        <small className="form-text text-muted">
                          Show CRT safety warning message on this product.
                        </small>
                      </div>
                    </div>
                  )}
                  {product.showcaseCategory === 'CRT' && role !== 'super' && product.showCrtWarning && (
                    <div className="form-group">
                      <small className="text-muted">
                        CRT safety warning is enabled for this product (only editable by supadmin).
                      </small>
                    </div>
                  )}
                  <div className="form-group">
                    <label>Author Name</label>
                    <input
                      type="text"
                      name="author_name"
                      placeholder=""
                      className="form-control"
                      value={product.author_name || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                </>
              )}
              <div className="form-group">
                <label>Intro
                  <br />
                  <small className="form-text text-muted">
                    {product.intro?.length || 0}/300 characters
                  </small>
                </label>
                <textarea
                  name="intro"
                  className="form-control"
                  value={product.intro || ''}
                  onChange={handleInputChange}
                  rows="6"
                />
              </div>
              <div className="form-group">
                <label>Description <span className="text-danger">*</span></label>
                <textarea
                  name="description"
                  className="form-control"
                  value={product.description || ''}
                  onChange={handleInputChange}
                  rows="6"
                />
              </div>
              {product.showcaseCategory === 'CRT' || product.category === 'CRT TV' ? (
                <>
                  {/* Multiple CRT Models - using only this field for all models */}
                  <div className="form-group">
                    <label>Compatible CRT Models</label>
                    <CreatableSelect
                    styles={{ container: (provided) => ({ ...provided, width: '100%' }) }}
                      isMulti
                      isClearable
                      isSearchable
                      isLoading={isLoadingModels}
                      options={suggestions}
                      value={product.crt_models && product.crt_models.map(model => ({
                        value: model.model || model.name,
                        label: model.model || model.name,
                        slug: model.slug || null
                      }))}
                      onInputChange={(inputValue, { action }) => {
                        if (action === 'input-change') {
                          setModelInput(inputValue);
                          debouncedFetch(inputValue);
                        }
                        if (action === 'menu-close') {
                          setModelInput('');
                        }
                      }}
                      inputValue={modelInput}
                      onChange={(selected) => {
                        setProduct((prev) => ({
                          ...prev,
                          crt_models: selected.map(opt => ({
                            name: opt.value,
                            model: opt.value,
                            slug: opt.slug || null,
                          }))
                        }));
                      }}
                      placeholder="Add compatible CRT models..."
                      formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                      classNamePrefix="dark-select"
                    />
                  </div>

                  {product.crt_models?.length > 0 && (
                    <div className="form-group">
                      <label>CRT Details</label>
                      <div style={{width: '100%'}}>
                        {product.crt_models.map((crt, idx) =>
                          crt.slug ? (
                            <div key={idx}>
                              <small>
                                <a
                                  href={`/crts/${crt.slug}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {window.location.origin}/crts/{crt.slug}
                                </a>
                              </small>
                            </div>
                          ) : null
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="form-group">
                  <label>Model</label>
                  <input
                    type="text"
                    name="model"
                    className="form-control"
                    value={product.model}
                    onChange={handleInputChange}
                    placeholder="Enter model"
                  />
                </div>
              )}
              {role === 'super' && (
              <div className="form-group">
                <label>Priority</label>
                <input
                  type="text"
                  name="priority"
                  className="form-control"
                  value={product.priority || ''}
                  onChange={handleInputChange}
                  onWheel={(e) => e.target.blur()}
                />
              </div>
              )}  
              
              {(product.type === 'sale' || product.type === 'service' || product.type === 'showcase') && (
                <>
                  <h5 className="headline paypal-header">
                    <div className="d-flex align-items-center">
                      PayPal Direct Button
                    </div>
                    <div className="d-flex align-items-center">
                      <label className="custom-toggle mb-0">
                        <input
                          type="checkbox"
                          checked={product.enablePaypalButton}
                          onChange={(e) => {
                            const { checked } = e.target;
                            setProduct((prevProduct) => ({
                              ...prevProduct,
                              enablePaypalButton: checked,
                            }));
                          }}
                        />
                        <span className="slider" />
                      </label>
                    </div>
                  </h5>
                  <small className="text-muted d-block mb-3">
                    Only enable this if you are planning to sell this item. Price and inventory must be set to display the PayPal button. Dynamic shipping rates are not currently supported, so be sure to include shipping costs in the item price.
                  </small>
                  {product.enablePaypalButton && (
                    <div className="pt-2">
                      <div className="form-group">
                        <label>PayPal Email</label>
                        <input
                          type="email"
                          name="paypalEmail"
                          className="form-control"
                          value={product.paypalEmail}
                          onChange={handleInputChange}
                          placeholder="Enter your PayPal email address"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Button Preview</label>
                        <div className="form-control paypal-preview-container p-3">
                          {product.paypalEmail && product.price ? (
                            <>
                              <div className="mb-2 text-info">
                                <strong>Item:</strong> {product.name}<br/>
                                <strong>Price:</strong> ${parseFloat(product.price).toFixed(2)}<br/>
                                <strong>PayPal Email:</strong> {product.paypalEmail}
                              </div>
                              
                              {/* Simple PayPal Button */}
                              <a 
                                href={`https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=${encodeURIComponent(product.paypalEmail)}&item_name=${encodeURIComponent(product.name)}&amount=${parseFloat(product.price).toFixed(2)}&currency_code=${product.currency ? product.currency.toUpperCase() : 'USD'}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="d-inline-block"
                                style={{ width: '100%' }}
                              >
                                <button 
                                  className="btn text-center d-flex align-items-center justify-content-center"
                                  style={{ 
                                    width: '100%', 
                                    backgroundColor: '#ffd140',
                                    color: '#000',
                                    padding: '8px 12px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    maxWidth: '400px'
                                  }}
                                >
                                  <img 
                                    src="/paypal.svg" 
                                    alt="PayPal" 
                                    style={{ height: '24px' }} 
                                  />
                                </button>
                              </a>
                              
                              <div className="mt-2">
                                <small className="text-muted">
                                  <i className="fa fa-info-circle"></i> Click the button to test PayPal checkout in a new window
                                </small>
                              </div>
                            </>
                          ) : (
                            <div className="text-muted">Please fill in all required fields to see the button preview</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {(product.type === 'sale' || product.type === 'service' || (product.type === 'showcase' && product.enablePaypalButton)) && (
                <>
                  <div className="form-group">
                    <label>Price <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      name="price"
                      className="form-control"
                      value={product.price || ''} // Convert cents to dollars
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Currency <span className="text-danger">*</span></label>
                    <select
                      name="currency"
                      className="form-select"
                      value={product.currency || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Currency</option>
                      <option value="USD">USD - US Dollar</option>
                    </select>
                  </div>
                </>
              )}


              {(product.type === 'affiliate') && (
                <>
                  <div className="form-group">
                    <label>Affiliate link</label>
                    <input
                      type="text"
                      name="affiliateLink"
                      className="form-control"
                      value={product.affiliateLink || ''} 
                      onChange={handleInputChange}
                      placeholder='Enter Affiliate Link'
                      required={product.type === 'affiliate'} // Make required when Affiliate is selected
                    />
                  </div>
                  <div className="form-group">
                    <label>Affiliate currency</label>
                    <select
                      name="affiliate-currency"
                      className="form-select"
                      value={product.affiliateCurrency || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Currency</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="CAD">CAD - Canadian Dollar</option>
                      <option value="AUD">AUD - Australian Dollar</option>
                      <option value="GBP">GBP - British Pound</option>
                    </select>
                  </div>
                </>
              )}
              {(product.type === 'sale' || product.type === 'service' || (product.type === 'showcase' && product.enablePaypalButton)) && (
              <>
                <h5 className="headline">Shipping Related</h5>
                <p>Note: Below are needed for shipping purposes. {!store.stripe_account_id && "Online options are hidden as Stripe is not setup"}</p>
                <div className="form-group">
                  <label>Availability</label>
                  <div style={{width:'100%'}}>
                    <div className="form-check">
                      <input
                        type="radio"
                        id="local_only"
                        name="availability"
                        value="local_only"
                        className="form-check-input"
                        checked={product.availability === 'local_only'}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="local_only" style={{textAlign:'left'}}>
                        Local Pickup Only
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        id="local_and_online"
                        name="availability"
                        value="local_and_online"
                        className="form-check-input"
                        checked={product.availability === 'local_and_online'}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="local_and_online" style={{textAlign:'left'}}>
                        Local & Online
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        id="online_only"
                        name="availability"
                        value="online_only"
                        className="form-check-input"
                        checked={product.availability === 'online_only'}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="online_only" style={{textAlign:'left'}}>
                        Online Only
                      </label>
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>Weight (lbs)</label>
                  <input
                    type="text"
                    name="weight"
                    className="form-control"
                    value={product.weight || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Inventory <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    name="inventory"
                    className="form-control"
                    value={product.inventory || ''}
                    onChange={handleInputChange}
                    onWheel={(e) => e.target.blur()}
                  />
                </div>
                {store.stripe_account_id && (
                  <>
                    <div className="form-group">
                      <label>HS Code</label>
                      <input
                        type="text"
                        name="hs_code"
                        className="form-control"
                        value={product.hs_code || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Country of Origin</label>
                      <input
                        type="text"
                        name="country_of_origin"
                        className="form-control"
                        value={product.country_of_origin || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Email template
                        <br />
                        <small className="form-text">
                          Email body that is sent when item is shipped.
                        </small>
                      </label>
                      <textarea
                        name="emailTemplate"
                        className="form-control"
                        value={product.emailTemplate || ''}
                        onChange={handleInputChange}
                        rows="6"
                      />
                    </div>
                  </>
                )}
              </>
              )}

              {(isEdit && (product.type === 'sale' || product.type === 'service') && store.stripe_account_id) && (
              <>
                <h5 className="headline">Stripe Custom Fields</h5>
                <p>Note: This field will be presented during checkout, so that you can get user input. For example, "CRT Model #". Maximum 3 custom fields are allowed per purchase. Therefore, if you don't need a custom field, leave this blank.</p>
                <div className="form-group">
                  <label>Stripe Custom Field</label>
                  <input
                    type="text"
                    name="label"
                    placeholder="e.g. CRT Model #"
                    className="form-control"
                    value={product.stripeCustomField.label}
                    onChange={handleCustomFieldChange}
                  />
                </div>
              </>
              )}
          </div>
          
          <div className="col-md-12 col-lg-6">
            {/* Right column content */}
            {isEdit && (
              <div className="media-section">
                <h5 className="headline">Media</h5>
                <p>Note: Spice up your product with some images and videos</p>
                <div className="form-group">
                  <label>YouTube video ID</label>
                  <input
                    type="text"
                    name="videos"
                    placeholder="ergbO_u12ABC"
                    className="form-control"
                    value={product.videos[0] || ''} // Show only the first video URL in the input field
                    onChange={(e) => {
                      const updatedVideos = [e.target.value]; // Store the video URL as an array
                      setProduct({ ...product, videos: updatedVideos });
                    }}
                  />
                </div>
                {/* YouTube Video Preview */}
                {product.videos[0] && (
                <div className="form-group">
                  <label></label>
                  <div className="video-preview" style={{width:'100%'}}>
                    <iframe
                      src={`https://www.youtube.com/embed/${product.videos[0]}`}
                      title="YouTube Video Preview"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="video-preview-iframe"
                    />
                  </div>
                </div>
                )}
                {uploadProgress > 0 && (
                  <div className="progress mb-3">
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{ 
                        width: `${uploadProgress}%`,
                        background: getProgressColor(uploadProgress), // dynamic styling for the bar
                        transition: 'width 0.4s ease-in-out', // Smooth width transitions
                        color: '#fff', // White text
                        fontWeight: 'bold', // Bold text for readability
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      aria-valuenow={uploadProgress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      {uploadProgress}%
                    </div>
                  </div>
                )}
                
                {/* Enhanced drag and drop file upload */}
                <div className="form-group">
                  <DragDropImageUpload onImageSelect={handleImageSelection} />
                </div>
                
                {/* Re-arrange Images section - will be made sticky */}
                <div id="rearrange-section" className="rearrange-section">
                  <h5 className="headline">Re-arrange Images</h5>
                  <p>Note: First image in the below set will be the hero image. <b>Click and drag to rearrange!</b></p>
                  <ThumbnailRearrange
                    imagePreviews={imagePreviews.map((image, index) => ({
                      ...image,
                      id: image.key || `image-${index}`,
                      url: image.preview || image.cropped_url || image.compressed_url || image.url, 
                      caption: image.caption || '', // Ensure captions are included
                    }))}
                    onReorder={async (newOrder) => {
                      setImagePreviews(newOrder);
                      setProduct((prevProduct) => ({
                        ...prevProduct,
                        images: newOrder,
                      }));
                  
                      // Auto-save product after rearrange
                      await updateProductImages(newOrder);
                    }}
                    onRemove={!product.published || role === 'super' ? handleRemoveImageById : null}
                    onThumbnailClick={(imageId) => {
                      console.log("Thumbnail clicked:", imageId);
                      
                      // Extract the index from the imageId if it follows the pattern 'image-X'
                      let index;
                      if (imageId.startsWith('image-')) {
                        index = parseInt(imageId.split('-')[1]);
                      } else {
                        // Find by key if not using the image-X pattern
                        index = imagePreviews.findIndex(img => img.key === imageId);
                      }
                      
                      console.log("Image index:", index);
                      
                      // Directly scroll to the specific image
                      const previewItems = document.querySelectorAll('.grid-item');
                      if (previewItems && previewItems.length > index && index !== -1) {
                        // Get the element and its position
                        const targetElement = previewItems[index];
                        
                        // Get the element's position relative to the viewport
                        const rect = targetElement.getBoundingClientRect();
                        
                        // Calculate the absolute position to scroll to (current scroll position + element's top position - offset)
                        const scrollToY = window.pageYOffset + rect.top - 120; // 120px offset from the top
                        
                        // Scroll to the calculated position
                        window.scrollTo({
                          top: scrollToY,
                          behavior: 'smooth'
                        });
                        
                        // Add a highlight effect to the card container instead of the grid item
                        const cardContainer = targetElement.querySelector('.card-container');
                        if (cardContainer) {
                          cardContainer.classList.add('highlight-effect');
                          setTimeout(() => {
                            cardContainer.classList.remove('highlight-effect');
                          }, 2000);
                        }
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* New section for Image Previews with left column layout */}

          <div className="row mt-4">
            <div className="col-md-12 col-lg-6">
              <div className="image-preview-section">
                <h5 className="headline">Image Previews</h5>
                {isEdit ? (
                  imagePreviews && imagePreviews.length > 0 ? (
                  <div className="grid-container">
                    {imagePreviews.map((image, idx) => {
                      const imageUrl = image.preview || image.compressed_url || image.url; // Use the appropriate URL for staged or existing images
                    
                      return (
                        <div key={idx} className="grid-item mb-4">
                          <div className="card-container">
                            <div className="image-wrapper position-relative">
                              {/* Image number indicator */}
                              <div className="image-number-badge">
                                {idx + 1}
                              </div>

                              <img src={imageUrl} alt={`Preview ${idx + 1}`} />
                              {/* Removed long press indicator and trash button */}
                            </div>
                            <div className="description-container">
                              <textarea 
                                className="description-editable" 
                                tabIndex="22" 
                                placeholder="Add a description" 
                                value={image.caption || ''} 
                                onChange={(e) => handleCaptionChange(idx, e.target.value)}
                                style={{
                                  border: 'none',
                                  padding: '14px',
                                  fontSize: '16px',
                                  width: '100%',
                                  boxShadow: 'none',
                                  outline: 'none'
                                }}
                                onInput={(e) => {
                                  e.target.style.height = 'auto';
                                  e.target.style.height = `${e.target.scrollHeight}px`;
                                }}
                                ref={(textarea) => {
                                  if (textarea) {
                                    textarea.style.height = 'auto';
                                    textarea.style.height = `${textarea.scrollHeight}px`;
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  ) : (
                    <p>No images added.</p>
                  )
                ) : (
                  <p className="text-muted">
                    {product.type === 'showcase' ? 'You can add images after creating the showcase item.' : 'You can add images after creating the product.'}
                  </p>
                )}
              </div>
            </div>
            
            {/* Right sidebar that will be sticky on desktop */}
            <div className="col-md-12 col-lg-6 d-none d-lg-block">
              {/* Remove the sticky sidebar content since we're making the original sticky */}
            </div>
          </div>
          
        <div>
          {isEdit && !!product.store_handle && !!product.slug ? (
            <div className="d-flex justify-content-between align-items-center">
              <h6><a href={`${ELECTRON_URL}/${product.store_handle}/product/${product.slug}`} target='_blank'>Preview</a></h6>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    &nbsp; 'Updating...'
                  </>
                ) : (
                  getButtonText()
                )}
              </button>
            </div>
          ) : (
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  &nbsp;'Adding...'
                </>
              ) : (
                getButtonText()
              )}
            </button>
          )}
        </div>

      </form>
    </Container>
  );
}

export default AddOrEditProduct;